"use client";

import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";

export type GlobalStakeStats = {
  totalUvmSupplyWei: bigint; // UVM.totalSupply
  totalUvmStakedWei: bigint; // STAKE.TOTAL_UVM_STAKED (live)
  totalUvmRewardedWei: bigint; // STAKE.TOTAL_REWARD (cumulative net)
  totalLandStaked: number; // STAKE.TOTAL_LAND_STAKED (live count)
  totalDnmStakedWei: bigint; // STAKE.TOTAL_DNM_STAKED (live)
  stakeholderCount: number; // STAKE.USER_LIST_ID (lifetime unique stakers)
  stakeCount: number; // STAKE.STAKE_LIST_ID (lifetime stakes; not displayed)
};

/**
 * One multicall over 6 STAKE getters + UVM.totalSupply for the Tab 2
 * Global Stats panel. Read-only, no backend. Because Radix unmounts the
 * inactive <TabsContent>, this only runs when Tab 2 is activated; the
 * optional `enabled` gate is a fallback if that ever changes.
 */
export function useGlobalStakeStats(opts?: { enabled?: boolean }) {
  const publicClient = usePublicClient();
  const enabled = (opts?.enabled ?? true) && !!publicClient;

  const query = useQuery({
    queryKey: ["stake", "global-stats"],
    staleTime: 0,
    enabled,
    queryFn: async (): Promise<GlobalStakeStats> => {
      if (!publicClient) {
        throw new Error("Global stats fetch prereqs missing");
      }
      const reads = await publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "TOTAL_LAND_STAKED" as const,
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "TOTAL_DNM_STAKED" as const,
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "TOTAL_UVM_STAKED" as const,
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "TOTAL_REWARD" as const,
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "USER_LIST_ID" as const,
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "STAKE_LIST_ID" as const,
          },
          {
            address: ContractAddress.UVM,
            abi: ERC20_ABI,
            functionName: "totalSupply" as const,
          },
        ],
        allowFailure: false,
      });
      return {
        totalLandStaked: Number(reads[0] as bigint),
        totalDnmStakedWei: reads[1] as bigint,
        totalUvmStakedWei: reads[2] as bigint,
        totalUvmRewardedWei: reads[3] as bigint,
        stakeholderCount: Number(reads[4] as bigint),
        stakeCount: Number(reads[5] as bigint),
        totalUvmSupplyWei: reads[6] as bigint,
      };
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
